import { Card, CardBody, Stacked, Typography } from '@statsbomb/kitbag-components';
import {
  CustomShapeLayer,
  PitchViz,
  GridSelectionLayer,
  BOX_PLUS_CHANNELS_GRID_ZONE_GROUPS,
  GridBumpersLayer,
  BOX_PLUS_CHANNELS_GRID_ZONES,
  GridColoursLayer,
  CustomShape,
} from '@statsbomb/kitbag-datavis';

import { useFilters } from '@statsbomb/kitbag-global-football';
import { useTranslation } from 'react-i18next';
import { ResetButton } from '../button/ResetButton';
import { toggleFilterZoneGroups } from './PitchFilters.helpers';

type PitchZoneFilterKey = 'pitchStartZones' | 'pitchEndZones';

const PitchFilterControl = ({
  selectedZones,
  setSelectedZones,
}: {
  selectedZones: CustomShape[];
  setSelectedZones: (zones: CustomShape[]) => void;
}) => (
  <GridBumpersLayer
    onBumperClick={(_, { ids }) => setSelectedZones(toggleFilterZoneGroups(ids, selectedZones))}
    gridZoneGroups={BOX_PLUS_CHANNELS_GRID_ZONE_GROUPS}
  >
    <PitchViz
      underlay={
        <>
          <GridColoursLayer zones={BOX_PLUS_CHANNELS_GRID_ZONES} />
          <CustomShapeLayer shapes={selectedZones} shapeRendering="crispEdges" />
          <GridSelectionLayer
            zones={BOX_PLUS_CHANNELS_GRID_ZONES}
            onZoneClick={(_, { id, points }) => {
              if (selectedZones.some(shape => shape.id === id)) {
                setSelectedZones(selectedZones.filter(shape => shape.id !== id));
              } else {
                setSelectedZones([{ id, points }, ...selectedZones]);
              }
            }}
          />
        </>
      }
    />
  </GridBumpersLayer>
);

const PitchFilterGroup = ({ filterKey, label }: { filterKey: PitchZoneFilterKey; label: string }) => {
  const [filters, setFilters] = useFilters('main');
  const selectedZones = filters[filterKey];
  const setSelectedZones = (zones: CustomShape[]) => setFilters({ ...filters, [filterKey]: zones });
  const { t } = useTranslation(['filters', 'general']);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="flex-grow">
          <Typography variant="headingMedium">{t(label, { ns: 'general' })}</Typography>
        </div>
        <span data-testid={`pitch-filter-reset-${label}`}>
          <ResetButton handleClick={() => setSelectedZones([])} />
        </span>
      </div>
      <span data-testid={`pitch-filter-${label}`}>
        <PitchFilterControl selectedZones={selectedZones} setSelectedZones={setSelectedZones} />
      </span>
    </div>
  );
};

export const PitchFiltersContent = () => (
  <Stacked direction="column" gap="medium">
    <PitchFilterGroup filterKey="pitchStartZones" label="start" />
    <PitchFilterGroup filterKey="pitchEndZones" label="end" />
  </Stacked>
);

export const PitchFilters = () => (
  <Card>
    <CardBody>
      <Stacked direction="column" gap="medium">
        <PitchFiltersContent />
      </Stacked>
    </CardBody>
  </Card>
);
